import {mapChat, mapChatMessage, mapSocketChatsToApp} from './mappers'
import type {
  ChatData,
  ChatMessage,
  ChatMessageDTO,
  WebSocketConnectionUser,
  WsEvents,
} from 'chats'
import {chatEventEmitter, Chats, globalChatsStore} from 'chats'

const defaultChatEndpoint = 'wss://backend.venera.city/ws'
const getMsg = (method: string, params = {}, options = {customId: ''}) => {
  return JSON.stringify({method, params, id: options.customId || method})
}
export enum ChatOutgoingEvent {
  login = 'login',
  blockUser = 'report_user',
  createChat = 'start_chat',
  deleteChat = 'delete_chat',
  openChat = 'open_chat',
  supportChat = 'support',
  chatsList = 'chat_list',
  sendMessage = 'send_message',
  deleteMessage = 'delete_message',
  sendLocation = 'send_location',

  // Your message has been sent
  messageReceived = 'delivered_message_send',
  // When message has been seen
  messageSeen = 'read_message_send',
  ping = 'ping',
  pong = 'pong',
  postMessage = 'post_message',
  readMessage = 'read_message',
}

export enum ChatIncomingEvent {
  login = 'login',
  connect = 'connect',
  blockUser = 'report_user',
  createChat = 'start_chat',
  deleteChat = 'delete_chat',
  openChat = 'open_chat',
  newChat = 'new_chat',
  supportChat = 'support',
  chatsList = 'chat_list',
  message = 'message_receive',
  sendMessage = 'send_message',
  sendLocation = 'send_location',
  unreadMessages = 'total_unread_messages',
  sendImageParams = 'send_image',
  uploadImage = 'send_image_upload',
  ping = 'ping',
  pong = 'pong',
  postMessage = 'post_message',
  readMessage = 'read_message',

  // Your message has been received
  messageReceived = 'delivered_message_receive',
  // Your message has been read
  messageRead = 'read_message_receive',
}

export class ChatsWebsockets {
  isInConnection = false
  onWsConnected?: () => void
  error?: any
  private user?: WebSocketConnectionUser
  private client: any

  constructor() {
    console.log('ChatsWebsockets.constructor')
  }

  get isConnected(): boolean {
    return this.client?.readyState === 1
  }

  private get requiredUser(): WebSocketConnectionUser {
    if (!this.user)
      throw new Error('user is undefined, maybe you forget call start(user)')
    return this.user
  }

  private getChatsEndpoint = (): string =>
    Chats.options.chatsEndpointOverrides ?? defaultChatEndpoint

  start(user: WebSocketConnectionUser): Promise<string | undefined> {
    return new Promise((resolve) => {
      console.log('ChatsWebsockets.start')
      this.user = user
      this.error = undefined
      if (this.isConnected) {
        this.onJabberReady()
        resolve(undefined)
        console.log('ChatController.startWebsockets do nothing')
        return
      }
      this.isInConnection = true
      this.client = new WebSocket(this.getChatsEndpoint())

      const onLogin = (err: any) => {
        console.log('ChatsWebsockets.onLogin', 'error:', err)
        if (!err) {
          this.onJabberReady()
          resolve(undefined)
        } else {
          this.onWsError(err)
          this.cleanup()
          resolve(err.toString())
        }
      }
      this.client.onopen = () => {
        this.client.send(getMsg(ChatOutgoingEvent.login, this.requiredUser))
      }
      this.client.onclose = (e: any) => {
        console.log('Disconnected', new Date())

        this.onWsError(e)
        globalChatsStore.closeConnection()
        setTimeout(() => {
          globalChatsStore.startConnection()
        }, 5000)
      }
      this.client.onmessage = (event: {data: string}) => {
        const data = JSON.parse(event.data)
        console.log('onmessage', data)

        // response messages
        if (data.id) {
          switch (data.id) {
            case ChatIncomingEvent.login: {
              onLogin(data.error)
              break
            }
            case ChatIncomingEvent.pong: {
              break
            }
            default: {
              console.warn('Unhandled response', data)
            }
          }
        }

        // events
        if (data.event_type) {
          switch (data.event_type) {
            case ChatIncomingEvent.newChat: {
              this.chatsList()
              break
            }
            case ChatIncomingEvent.postMessage: {
              const m = mapChatMessage(data.data)
              chatEventEmitter.trigger('newMessage', m)
              break
            }
            case ChatIncomingEvent.ping: {
              this.client.send(getMsg(ChatOutgoingEvent.pong))
              break
            }
            default: {
              console.warn('Unhandled event', data)
            }
          }
        }
      }

      // this.client.on(ChatIncomingEvent.connect, onConnect)
      // this.client.on(ChatIncomingEvent.login, onLogin)
      // this.client.on(ChatIncomingEvent.message, this.onNewMessage)
      // this.client.on(
      //   ChatIncomingEvent.unreadMessages,
      //   this.onUnreadMessagesCount,
      // )
      // this.client.on(ChatIncomingEvent.messageRead, this.onMessageRead)
      // this.client.on(ChatIncomingEvent.messageReceived, this.onMessageReceived)
    })
  }

  private onWsError = (error: any) => {
    this.error = error
    this.isInConnection = false
    this.trigger('error', error)
    this.sendConnectedStatus()
  }

  private onJabberReady = () => {
    console.log('ChatController.onJabberReady')
    this.isInConnection = false
    this.sendConnectedStatus()
    this.chatsList()
    this.onWsConnected?.()
    this.onWsConnected = undefined
  }

  private sendConnectedStatus = () => {
    const isConnected = this.isConnected
    globalChatsStore.setWsConnectionStatus(isConnected)
    chatEventEmitter.trigger('onSocketConnectionChanged', isConnected)
  }

  private async once(endpoint: ChatIncomingEvent): Promise<any> {
    return new Promise((resolve, reject) => {
      this.client.once(endpoint, (error: any, data: any) => {
        console.log('ChatsWebsockets.once', {endpoint, error, data})
        if (data) return resolve(data)
        else reject(error)
      })
    })
  }

  chatsList = async () => {
    console.log('ChatsWebsockets.chatsList')
    try {
      const data = await Chats.api.chat.fetchChats()
      console.log('data', data)

      const chats = mapSocketChatsToApp(data.result, this.requiredUser)

      this.trigger('updateChatsList', chats)
    } catch (e) {
      console.error(e)
    }
  }

  fetchMessages = async (params: {
    chat_id: string
    since?: number
  }): Promise<Array<ChatMessage>> => {
    console.log('ChatsWebsockets.fetchMessages', 'chatId', params)
    const {chat_id} = params
    try {
      const data = await Chats.api.chat.fetchChatMessages(chat_id)
      console.log('data', data)
      const messages = data.result.map((m: ChatMessageDTO) => mapChatMessage(m))
      return messages
    } catch (e) {
      console.error(e)
      return []
    }
  }

  fetchChat = async (chatId: string): Promise<ChatData | undefined> => {
    console.log('ChatsWebsockets.fetchChat')
    try {
      const data = await Chats.api.chat.fetchChat(chatId)
      return mapChat(data.result, this.requiredUser)
    } catch (e) {
      console.error('ChatsWebsockets.fetchChat', e)
      return undefined
    }
  }

  deleteChat = async (chat_id: string) => {
    try {
      const data = await Chats.api.chat.deleteChat(chat_id)
      console.log('data', data)
    } catch (e) {
      console.error(e)
    }
  }

  deleteMessage(messageId: string, fullDeletion: boolean = true) {
    this.client.emit(
      ChatOutgoingEvent.deleteMessage,
      this.requiredUser,
      messageId,
      fullDeletion,
    )
  }

  async blockUser(interlocutorId: string): Promise<boolean> {
    if (!this.isConnected) return false
    console.log('ChatsWebsockets.blockUser', 'interlocutorId', interlocutorId)
    this.client.emit(
      ChatOutgoingEvent.blockUser,
      this.requiredUser,
      interlocutorId,
    )
    try {
      await this.once(ChatIncomingEvent.blockUser)
      this.trigger('blockUser', interlocutorId)
      return true
    } catch (e) {
      console.error(e, 'ChatsWebsockets.blockUser')
      this.trigger('onSendMessageError', e)
      return false
    }
  }

  sendTextMessage = async (
    chatId: string,
    message: string,
  ): Promise<ChatMessage | undefined> => {
    console.log('ChatsWebsockets.sendTextMessage')
    try {
      const data = await Chats.api.chat.createNewMessage({
        chatId,
        text: message,
      })
      const m = mapChatMessage(data.result.message)
      this.trigger('newMessage', m)
      return m
    } catch (e) {
      console.error(e)
      return undefined
    }
  }

  async sendLocationMessage(
    chatId: string,
    lat: number,
    lng: number,
  ): Promise<ChatMessage | undefined> {
    console.log(
      'ChatsWebsockets.sendLocationMessage',
      'lat',
      lat,
      'lng',
      lng,
      'user',
      this.requiredUser,
    )
    this.client.emit(
      ChatOutgoingEvent.sendLocation,
      this.requiredUser,
      chatId,
      lat,
      lng,
    )

    try {
      const message: ChatMessageDTO = await this.once(
        ChatIncomingEvent.sendLocation,
      )
      const m = mapChatMessage(message)
      this.trigger('newMessage', m)
      return m
    } catch (e) {
      console.error(e, 'ChatsWebsockets.sendLocationMessage')
      this.trigger('onSendMessageError', e)
      return undefined
    }
  }

  createChat = async (params: {
    productHash: string
  }): Promise<ChatData | undefined> => {
    console.log('ChatsWebsockets.newChat', 'params', params)

    try {
      const data = await Chats.api.chat.createChat(params)
      const result = data.result
      const now = Date.now() * 1000
      return mapChat(
        {
          ...result,
          id: result.chat_id,
          date_created: now,
          date_last_active: now,
          last_message: {},
          unread_messages: 0,
        },
        this.requiredUser,
      )
    } catch (e) {
      console.error(e, 'ChatsWebsockets.createChat')
      return undefined
    }
  }

  async createSupportChat(): Promise<ChatData | undefined> {
    console.log('ChatsWebsockets.createSupportChat')
    if (!this.isConnected) return undefined

    this.client.emit(ChatOutgoingEvent.supportChat, this.requiredUser)
    try {
      const data = await this.once(ChatIncomingEvent.supportChat)
      return mapChat(data, this.requiredUser)
    } catch (e) {
      console.error(e, 'ChatsWebsockets.createSupportChat')
      return undefined
    }
  }

  async sendSeen({messageId, chatId}: {messageId: string; chatId: string}) {
    console.log(
      'ChatsWebsockets.sendSeen',
      'messageId',
      messageId,
      'chat_id',
      chatId,
    )
    try {
      const data = await Chats.api.chat.markAsRead({messageId, chatId})
      console.log('data', data)
      return data.result
    } catch (e) {
      console.error(e, 'ChatsWebsockets.sendSeen')
      return undefined
    }
  }

  async sendReceived(messageId: string) {
    console.log('ChatsWebsockets.sendReceived', 'messageId', messageId)
    // this.client.emit(
    //   ChatOutgoingEvent.messageReceived,
    //   this.requiredUser,
    //   messageId,
    // )
  }

  onNewMessage = (err: any, message: ChatMessageDTO) => {
    console.log(
      'ChatsWebsockets.onNewMessage',
      'error:',
      err,
      'message:',
      message,
    )
    this.sendReceived(message.id)
    const m = mapChatMessage(message)
    this.trigger('newMessage', m)
  }

  onUnreadMessagesCount = (_err: any, count: any) => {
    this.trigger('newMessagesCount', count)
  }

  onMessageRead = (_err: any, messageId: string) => {
    // console.log('🪄 On message read', messageId)
    chatEventEmitter.trigger('onMessageRead', messageId)
    console.log('ChatsWebsockets.onMessageRead', messageId)
  }

  onMessageReceived = (_err: any, messageId: string) => {
    chatEventEmitter.trigger('onMessageReceived', messageId)
    console.log('ChatsWebsockets.onMessageReceived', messageId)
  }

  trigger(label: WsEvents, ...args: any) {
    chatEventEmitter.trigger(label, ...args)
  }

  cleanup() {
    if (!this.client) return
    console.log('ChatsWebsockets.cleanup')
    this.client.close()
    this.client = undefined
    this.isInConnection = false
    this.sendConnectedStatus()
  }
}

export const chatsWebsockets = new ChatsWebsockets()
